// src/index.js

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import CookieConsent from './components/CookieConsent';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { BrowserRouter } from 'react-router-dom';
import { AppProvider } from './AppContext';
import { H } from 'highlight.run';
import { ErrorBoundary } from '@highlight-run/react';
import { SnackbarProvider, SnackbarContext } from './SnackbarContext';
import { AuthProvider } from './auth/AuthProvider';

// Shared color palette
const colorPalette = {
  primary:    { main: '#497ed7', lighter: '#92b2e7', light: '#6d98df', dark: '#3a65ac', darker: '#2c4c81', contrastText: '#ffffff' },
  secondary:  { main: '#41952a', lighter: '#8dbf7f', light: '#67aa55', dark: '#347722', darker: '#275919', contrastText: '#ffffff' },
  tertiary:   { main: '#9452d0', lighter: '#bf97e3', light: '#a975d9', dark: '#7642a6', darker: '#59317d', contrastText: '#ffffff' },
  quaternary: { main: 'rgb(120, 120, 120)', light: 'rgb(100, 100, 100)', dark: 'rgb(80, 80, 80)', contrastText: '#ffffff' },
  success:    { main: '#41952a', lighter: '#8dbf7f', light: '#67aa55', dark: '#347722', darker: '#275919', contrastText: '#ffffff' },
  info:       { main: '#9452d0', lighter: '#bf97e3', light: '#a975d9', dark: '#7642a6', darker: '#59317d', contrastText: '#ffffff' },
  warning:    { main: '#eb8b37', lighter: '#f3b987', light: '#efa25f', dark: '#bc6f2c', darker: '#8d5321', contrastText: '#ffffff' },
  error:      { main: '#eb3737', lighter: '#f38787', light: '#ef5f5f', dark: '#bc2c2c', darker: '#8d2121', contrastText: '#ffffff' },
};

// Light theme options using shared palette
const lightThemeOptions = {
  palette: {
    mode: 'light',
    ...colorPalette,
    background: {
      default: '#ffffff',
      paper:   '#f4f4f4',
      alt:     '#f8f8f8',
    },
    text: {
      primary:   '#000000',
      secondary: '#333333',
      alt:       '#888888',
    },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        size:   'small',
        margin: 'dense',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size:   'small',
        margin: 'dense',
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-container': {
            alignItems: 'flex-start',
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: '#333333',
          '&.Mui-checked': {
            color: '#333333',
          },
          '&.MuiCheckbox-indeterminate': {
            color: '#333333',
          },
        }),
      },
    },
  },
};

// Dark theme options using shared palette
const darkThemeOptions = {
  palette: {
    mode: 'dark',
    ...colorPalette,
    background: {
      default: '#1e1e1e',
      paper:   '#1e1e1e',
      alt:     '#444444',
    },
    text: {
      primary:   '#ffffff',
      secondary: '#ffffff',
      alt:       '#444444',
    },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        size:   'small',
        margin: 'dense',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size:   'small',
        margin: 'dense',
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-container': {
            alignItems: 'flex-start',
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: '#ffffff',
          '&.Mui-checked': {
            color: '#ffffff',
          },
          '&.MuiCheckbox-indeterminate': {
            color: '#ffffff',
          },
        }),
      },
    },
  },
};

// ----------------------------------------------------------------------------
// Inject GA4 script + init GA + initialize Highlight
// ----------------------------------------------------------------------------
function loadGAAndHighlight() {
  // 1) Inject GA4 script
  const gaScript = document.createElement('script');
  gaScript.async = true;
  gaScript.src   = 'https://www.googletagmanager.com/gtag/js?id=G-GZM46THV71';
  document.head.appendChild(gaScript);

  // 2) Configure gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', 'G-GZM46THV71', { send_page_view: false });

  // 3) Initialize Highlight with only console & error recording
  H.init("jgo9xnyg", {
    serviceName: "micgro-web",
    manualStart: true,
    tracingOrigins: [],
    networkRecording:   { enabled: false },
    consoleRecording: {
      enabled: true,
      consoleMethodsToRecord: ['log','warn','error','info'],
    },
    errorRecording: { enabled: true },
  });
}

// ----------------------------------------------------------------------------
function AuthNotifyBridge({ children }) {
  const { showSuccess, showError } = useContext(SnackbarContext);

  const notify = useCallback(
    (msg, variant) => {
      if (variant === 'success') {
        showSuccess?.(msg);
        return;
      }
      if (variant === 'warning') {
        showError?.(msg);
        return;
      }
      showError?.(msg);
    },
    [showSuccess, showError]
  );

  return <AuthProvider notify={notify}>{children}</AuthProvider>;
}

// Root component wraps the theme, AppProvider, Router, and CookieConsent
// ----------------------------------------------------------------------------
function Root({ onAcceptGA }) {
  // Determine initial theme (localStorage or system preference)
  const getInitialTheme = () => {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) return stored === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const [darkMode, setDarkMode] = useState(getInitialTheme);

  // Persist theme preference
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Create MUI theme
  const theme = createTheme(darkMode ? darkThemeOptions : lightThemeOptions);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider>
        <AuthNotifyBridge>
          <AppProvider>
            {/* Consent banner */}
            <CookieConsent onAcceptGA={onAcceptGA} />

            <BrowserRouter>
              <App darkMode={darkMode} setDarkMode={setDarkMode} />
            </BrowserRouter>
          </AppProvider>
        </AuthNotifyBridge>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

// ----------------------------------------------------------------------------
// Mount the React tree
// ----------------------------------------------------------------------------
const container = document.getElementById('root');
const root      = createRoot(container);

root.render(
  <React.StrictMode>
    <ErrorBoundary fallback={<div>Something went wrong.</div>}>
      <Root onAcceptGA={loadGAAndHighlight} />
    </ErrorBoundary>
  </React.StrictMode>
);

reportWebVitals();
